import { useEffect, useState } from "react";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getStorage, ref, getDownloadURL } from "firebase/storage";
import { useNavigate } from "react-router-dom";
import { detectLang, t } from "../i18n";
import LoadingSpinner from "./LoadingSpinner";
import "./Cursos.css";

const modulosInfo = {
  es: [
    { id: "modulo1", titulo: "¿Cómo me registro?", lecciones: 1 },
    { id: "modulo2", titulo: "Panel de trabajo", lecciones: 5 },
    { id: "modulo3", titulo: "Crea tu comunidad", lecciones: 4 },
    { id: "modulo4", titulo: "¿Cómo creo contenido?", lecciones: 3 },
    { id: "modulo5", titulo: "Bonus", lecciones: 2 },
  ],
  en: [
    { id: "modulo1", titulo: "How do I register?", lecciones: 1 },
    { id: "modulo2", titulo: "Work panel", lecciones: 5 },
    { id: "modulo3", titulo: "Build your community", lecciones: 4 },
    { id: "modulo4", titulo: "How do I create content?", lecciones: 3 },
    { id: "modulo5", titulo: "Bonus", lecciones: 2 },
  ],
};

const Cursos = () => {
  const [userName, setUserName] = useState("");
  const [imagenes, setImagenes] = useState({});
  const [loading, setLoading] = useState(true);
  const [lang] = useState(detectLang());
  const navigate = useNavigate();
  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const db = getFirestore();
        const userDoc = await getDoc(doc(db, "usuarios", user.uid));
        if (userDoc.exists()) {
          setUserName(userDoc.data().nombre || "Usuario");
        }
        await cargarImagenes();
        setLoading(false);
      } else {
        navigate("/");
      }
    });
    return () => unsubscribe();
  }, [auth]);

  const cargarImagenes = async () => {
    const cacheKey = `cache_imagenes_modulos`;
    const cached = localStorage.getItem(cacheKey);

    if (cached) {
      setImagenes(JSON.parse(cached));
      return;
    }

    const storage = getStorage();
    const imgs = {};
    for (let i = 1; i <= 5; i++) {
      try {
        const url = await getDownloadURL(ref(storage, `cursos/mystake/imagenes/modulo${i}.png`));
        imgs[`modulo${i}`] = url;
      } catch {
        imgs[`modulo${i}`] = null;
      }
    }

    localStorage.setItem(cacheKey, JSON.stringify(imgs));
    setImagenes(imgs);
  };

  const handleLogout = () => {
    auth.signOut();
    navigate("/");
  };

  const modulos = modulosInfo[lang] || modulosInfo["es"];

  if (loading) return <LoadingSpinner />;

  return (
    <div className="biblioteca-screen">
      <nav className="nav">
        <div className="nav-brand">AFILIADOS<span>MARKET</span></div>
        <div className="nav-right">
          <div className="nav-user">
            <div className="nav-dot"></div>
            {userName}
          </div>
          <button className="btn-logout" onClick={handleLogout}>
            {t(lang, "library", "logout")}
          </button>
        </div>
      </nav>

      <div className="bib-body">
        <div className="bib-welcome">
          {t(lang, "library", "welcome")}, {userName} 👋
        </div>
        <div className="bib-title">
          {lang === "es" ? "Casino Affiliate Masterclass" : "Casino Affiliate Masterclass"}
        </div>
        <div className="bib-subtitle">
          {lang === "es"
            ? "Completá los módulos en orden para convertirte en afiliado profesional"
            : "Complete the modules in order to become a professional affiliate"}
        </div>

        <div className="progress-section">
          <div className="progress-info">
            <div className="progress-label">{t(lang, "library", "progress")}</div>
            <div className="progress-bar-bg">
              <div className="progress-bar-fill" style={{ width: "0%" }}></div>
            </div>
          </div>
          <div className="progress-pct">0%</div>
        </div>

        <div className="courses-grid">
          {modulos.map((modulo, index) => (
            <div
              key={modulo.id}
              className="course-card"
              onClick={() => navigate(`/curso/mystake/${modulo.id}`)}
            >
              <div className="course-thumb">
                {imagenes[modulo.id] ? (
                  <>
                    <img src={imagenes[modulo.id]} alt={modulo.titulo} />
                    <div className="course-thumb-overlay"></div>
                  </>
                ) : (
                  <div className="course-thumb-icon">
                    <svg viewBox="0 0 24 24" style={{ width: 28, height: 28, stroke: "#a855f7", fill: "none", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" }}>
                      <path d="M12 2L2 7l10 5 10-5-10-5z" />
                    </svg>
                  </div>
                )}
                <div className="course-tag">MÓDULO {index + 1}</div>
              </div>
              <div className="course-body">
                <div className="course-name">{modulo.titulo}</div>
                <div className="course-desc">
                  {modulo.lecciones} {lang === "es" ? "lecciones" : "lessons"}
                </div>
                <button className="btn-enter">
                  <svg viewBox="0 0 24 24" style={{ width: 12, height: 12, fill: "#fff" }}>
                    <polygon points="5 3 19 12 5 21 5 3" />
                  </svg>
                  {t(lang, "library", "enter")}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="motivacion-section" style={{ margin: "40px auto 0" }}>
        <div className="motivacion-icon">🚀</div>
        <div className="motivacion-titulo">
          {lang === "es" ? "Palabras para el camino" : "Words for the journey"}
        </div>
        <div className="motivacion-texto">
          {lang === "es"
            ? '"El éxito en el marketing de afiliados no llega de un día para el otro — llega para quienes no se rinden. Cada video que grabás, cada link que compartís, cada comunidad que construís es un ladrillo más en tu camino. El despegue no depende de la suerte, depende de vos. Los que viralizan no son los más talentosos, son los más constantes. Hoy das un paso, mañana das otro, y un día mirás para atrás y no podés creer hasta dónde llegaste. Las herramientas están acá. El conocimiento está acá. Solo falta una cosa: que vos decidas empezar."'
            : '"Success in affiliate marketing doesn\'t happen overnight — it comes to those who refuse to quit. Every video you record, every link you share, every community you build is one more step forward. Your breakthrough doesn\'t depend on luck, it depends on you. Those who go viral aren\'t the most talented — they\'re the most consistent. Today you take one step, tomorrow another, and one day you\'ll look back and barely recognize how far you\'ve come. The tools are here. The knowledge is here. All that\'s missing is your decision to start."'
          }
        </div>
        <div className="motivacion-firma">AfiliadosMarket</div>
      </div>

      <div className="footer">Copyright © 2025 AfiliadosMarket</div>
    </div>
  );
};

export default Cursos;